import { formatDistanceToNow, format as formatDate } from "date-fns";

import i18n from "@/shared/lib/i18n/index.ts";
import { dateFnsLocale } from "@/shared/lib/i18n/formatters.ts";

const invalidDate = () => i18n.t("common:date.invalid");

/** Parse ISO that might be missing a timezone; assume UTC if none is present */
const parseAssumingUTC = (isoMaybeNoTZ: string): Date => {
  // Has timezone? (Z or ±HH:MM)
  if (/[zZ]|[+\-]\d{2}:\d{2}$/.test(isoMaybeNoTZ)) return new Date(isoMaybeNoTZ);
  // No TZ given -> treat as UTC by appending Z
  return new Date(`${isoMaybeNoTZ}Z`);
};

/**
 * Correct: get relative time from a UTC ISO (or ISO without tz you want to treat as UTC)
 * Example input: "2025-08-07T15:54:25.000Z"
 */
export const formatRelativeTimeUTC = (utcIso: string): string => {
  const date = parseAssumingUTC(utcIso);
  if (isNaN(date.getTime())) return invalidDate();
  return formatDistanceToNow(date, { addSuffix: true, locale: dateFnsLocale() });
};

/**
 * If you actually have a LOCAL datetime string (no timezone) and want the *UTC instant*
 * that those local wall-clock components represent, convert it to a proper UTC ISO.
 * Example input (local time): "2025-08-07T15:54:25"
 */
export const localISOToUTCISO = (localIsoNoTZ: string): string => {
  // This new Date(...) treats the string as LOCAL time
  const local = new Date(localIsoNoTZ);
  if (isNaN(local.getTime())) return invalidDate();

  // Build the UTC timestamp from the local components:
  const utcMs = Date.UTC(
    local.getFullYear(),
    local.getMonth(),
    local.getDate(),
    local.getHours(),
    local.getMinutes(),
    local.getSeconds(),
    local.getMilliseconds(),
  );
  return new Date(utcMs).toISOString(); // normalized UTC ISO (with Z)
};

/**
 * Formats a UTC ISO to a local display format (no manual tz math needed)
 */
export const formatUTCDateToLocal = (utcIso: string, fmt: string): string => {
  const date = parseAssumingUTC(utcIso);
  if (isNaN(date.getTime())) return invalidDate();
  return formatDate(date, fmt, { locale: dateFnsLocale() });
};

/** Current user's timezone offset in hours (e.g. +2 for CEST) */
export const getTimezoneOffset = (): number => new Date().getTimezoneOffset() / -60;
